// Configuration des tutos affichés aux joueurs (élèves), un écran = une phase.
//
// Consommée par TutoRunner (src/tutos/TutoRunner.js). Chaque phase s'affiche au plus une fois par
// joueur (seenAt stocké en base dès l'ouverture). Une phase se déclenche soit par la route
// (`page`, préfixe d'URL — la plupart des écrans), soit par l'apparition d'un élément dans le DOM
// (`triggerElementId` — pour un écran qui n'est pas une page à part entière, ex. la fiche d'un
// actif, ouverte en modal par-dessus /portefeuille_menu).
//
// Interpolation : {{username}} dans un title/description est remplacé par le username du joueur
// connecté à l'affichage.
//
// highlightedElementId (optionnel) : id HTML de l'élément à mettre en avant (contour blanc +
// filtre blanc translucide par-dessus, reste de la page assombri, scroll automatique si besoin).
// Accepte aussi un tableau d'ids candidats quand le même visuel existe en double dans le DOM selon
// le viewport (ex. la cloche notifications desktop/mobile) — le premier candidat effectivement
// visible est utilisé.

/**
 * @typedef {Object} TutoVisual
 * @property {string} title
 * @property {string} description
 * @property {string | string[]} [highlightedElementId]
 * @property {boolean} [mockPosition] - Affiche une carte de position fictive dans le drawer (entre le
 * texte et les flèches de navigation), pour un visuel qui explique une position ouverte alors que le
 * joueur n'en a peut-être pas encore une réelle.
 */

/**
 * @typedef {Object} TutoPhase
 * @property {string} name - Identifiant unique de la phase, stocké tel quel comme clé dans users.tutos_seen.
 * @property {string} label - Nom court affiché dans la liste "Revoir un tuto" (page Profil).
 * @property {string} [page] - Route sur laquelle la phase doit se déclencher (préfixe, ex. '/accueil').
 * @property {string} [triggerElementId] - Alternative à `page` : id dont l'apparition dans le DOM déclenche la phase.
 * @property {string} [revisitPath] - Route sur laquelle naviguer pour rejouer la phase depuis "Revoir un tuto",
 * pour une phase sans `page` (déclenchée par `triggerElementId`, ex. une modal ouverte depuis une autre page).
 * @property {TutoVisual[]} visuals - Écrans du tuto, dans l'ordre de présentation.
 */

/** @type {TutoPhase[]} */
export const tutosConfig = [
    // ── Écran 1 — Accueil ──
    {
        name: 'accueil_intro',
        label: 'Accueil',
        page: '/accueil',
        visuals: [
            {
                title: 'Bienvenue sur Trading Series, {{username}} !',
                description: "Pendant les prochains jours, tu vas gérer un portefeuille sur des marchés bien vivants. Deux minutes pour tout comprendre.",
            },
            {
                title: 'Ton capital total',
                description: "C'est la valeur de tout ce que tu possèdes : ton argent disponible plus la valeur de tes positions. C'est ce chiffre qui décide de ton classement.",
                highlightedElementId: 'tuto-capital-total',
            },
            {
                title: 'Ta performance',
                description: "Vert tu gagnes, rouge tu perds. Change la période pour voir si c'est un mauvais moment ou une mauvaise stratégie.",
                highlightedElementId: 'tuto-capital-graph',
            },
            {
                title: 'Liquidité et positions',
                description: "La liquidité, c'est le cash que tu peux encore investir. Les positions, c'est ce que tu as déjà placé sur les marchés. Tout à gauche, rien ne travaille pour toi.",
                highlightedElementId: 'tuto-liquidite-positions',
            },
            {
                title: 'Les défis du jour',
                description: "Chaque semaine, des objectifs à remplir. Réussis-les et tu empoches des liquidités supplémentaires à réinvestir. Il n'y en a peut-être pas encore, reviens vérifier plus tard.",
                highlightedElementId: 'tuto-defis-section',
            },
            {
                title: 'Ta réserve de savoir',
                description: "Des articles courts pour comprendre les bases, et d'autres plus pointus pour ceux qui veulent aller loin. Bloqué sur une notion ? La réponse est probablement ici.",
                highlightedElementId: 'tuto-apprendre-section',
            },
            {
                title: 'Ce qui se passe dans le monde',
                description: "Ici tu retrouves tous les événements qui ont secoué les marchés depuis le début de la partie. Une découverte minière, une faillite, une innovation : chaque actualité déplace des cours.",
                highlightedElementId: ['tuto-notification-bell-desktop-fixed', 'tuto-notification-bell-mobile'],
            },
            {
                title: 'Tu ne rateras rien',
                description: "Un email t'avertit à chaque événement important. Et à ta prochaine connexion, une vidéo te résume ce qui s'est passé pendant ton absence.",
                highlightedElementId: ['tuto-notification-bell-desktop-fixed', 'tuto-notification-bell-mobile'],
            },
        ],
    },

    // ── Écran 2 — Marchés ──
    {
        name: 'marches_intro',
        label: 'Marchés',
        page: '/portefeuille_menu',
        visuals: [
            {
                title: 'Le terrain de jeu',
                description: "C'est ici que tu trouves tout ce que tu peux acheter ou vendre. Recherche un actif par son nom, ou parcours les catégories.",
                highlightedElementId: 'marches-search-bar',
            },
            {
                title: 'Tes favoris',
                description: 'Mets en favori les actifs que tu surveilles. Tu les retrouves en un clic sans refouiller toute la liste.',
                highlightedElementId: 'marches-favoris-btn',
            },
            {
                title: "Six familles d'actifs",
                description: "Chaque famille a son rythme et ses risques. Trois clics pour comprendre laquelle te ressemble.",
                highlightedElementId: 'marches-category-pills',
            },
            {
                title: 'Les actions',
                description: "Une part d'entreprise : si la société performe, ton action monte. Paris pour le luxe et l'industrie, New York pour la tech et les gros volumes, Londres pour la banque et l'énergie.",
                highlightedElementId: 'marches-category-pills',
            },
            {
                title: 'Devises et matières premières',
                description: "Les devises, c'est parier sur une monnaie contre une autre : petits mouvements, donc terrain de prédilection du levier. Les matières premières (pétrole, or, blé) dépendent de l'offre, de la demande et de la géopolitique.",
                highlightedElementId: 'marches-category-pills',
            },
            {
                title: 'Les cryptomonnaies',
                description: "Le segment le plus volatil du jeu. Ça monte vite, ça descend encore plus vite. À manier avec la tête froide.",
                highlightedElementId: 'marches-category-pills',
            },
        ],
    },

    // ── Écran 3 — Fiche actif (pas une page à part entière : modal ouverte depuis Marchés) ──
    {
        name: 'fiche_actif_intro',
        label: 'Fiche actif',
        triggerElementId: 'trade-modal-root',
        revisitPath: '/portefeuille_menu',
        visuals: [
            {
                title: 'La cotation',
                description: "Le prix actuel de l'actif, et sa variation sur la période affichée. C'est ce prix qui sert de référence quand tu passes un ordre.",
                highlightedElementId: 'trade-quote',
            },
            {
                title: "Lire l'historique",
                description: "Zoome d'un an jusqu'à l'historique complet. Plus haut et plus bas te donnent l'amplitude : un actif qui bouge de 20 % dans la journée n'est pas le même pari qu'un actif calme.",
                highlightedElementId: 'trade-chart',
            },
            {
                title: 'Deux façons de gagner',
                description: "Long : tu achètes, tu gagnes si le prix monte. Short : tu vends un actif que tu ne possèdes pas, tu gagnes s'il baisse. Sur Trading Series, un marché qui s'effondre reste une opportunité.",
                highlightedElementId: 'trade-buy-sell-tabs',
            },
            {
                title: 'Combien tu engages',
                description: 'Choisis ton montant, ou utilise les raccourcis. Tu ne peux pas engager plus que ta liquidité disponible.',
                highlightedElementId: 'trade-amount-card',
            },
            {
                title: 'Le levier',
                description: "Avec un levier ×5, 500 € engagés se comportent comme 2 500 € sur le marché. Tes gains sont multipliés par 5. Tes pertes aussi. Une baisse de 20 % avec un levier ×5, et ta position est entièrement perdue.",
                highlightedElementId: 'trade-leverage-card',
            },
        ],
    },

    // ── Écran 4 — Classement ──
    {
        name: 'classement_intro',
        label: 'Classement',
        page: '/classement',
        visuals: [
            {
                title: 'Le podium',
                description: 'Les trois meilleurs portefeuilles de la partie, mis à jour en direct. Ta place dépend uniquement de ton capital total.',
                highlightedElementId: 'classement-podium',
            },
            {
                title: 'Deux classements',
                description: "Général : tous les joueurs, chacun pour soi. Équipe : la performance cumulée de ton groupe. Ta mauvaise journée pèse sur les autres, alors coordonnez-vous !",
                highlightedElementId: 'classement-tabs',
            },
        ],
    },

    // ── Écran 5 — Portefeuille ──
    {
        name: 'portefeuille_intro',
        label: 'Portefeuille',
        page: '/portefeuille',
        visuals: [
            {
                title: 'Ton portefeuille',
                description: "L'écran le plus important. Tu y vois en un coup d'œil combien tu as, et combien tu as gagné ou perdu depuis le départ.",
                highlightedElementId: 'pf-solde',
            },
            {
                title: 'Ta répartition',
                description: 'La part de ton capital sur chaque famille d\'actifs, et ce qui dort encore en liquidité. Tout miser sur une seule catégorie, c\'est jouer à quitte ou double.',
                highlightedElementId: 'pf-repartition',
            },
            {
                title: 'Tes positions ouvertes',
                description: "C'est là que tu retrouves toutes tes positions ouvertes, celles sur lesquelles tu es encore engagé.",
                highlightedElementId: 'pf-tab-positions',
            },
            {
                title: 'Une position en détail',
                description: "Le montant investi, ton P&L (ce que tu gagnes ou perds en direct) et la valeur actuelle de la position. Le badge indique ton sens et ton levier.",
                mockPosition: true,
            },
            {
                title: 'Reprendre la main',
                description: "Modifier pour ajuster ta position. Fermer pour encaisser : le gain ou la perte affiché devient définitif et retourne dans ta liquidité. Tant que tu ne fermes pas, rien n'est acquis.",
                mockPosition: true,
            },
            {
                title: 'Ton historique',
                description: "Toutes tes positions fermées. C'est là que tu comprends ce qui a marché et ce que tu ne dois plus refaire.",
                highlightedElementId: 'pf-tab-history',
            },
            {
                title: 'À toi de jouer.',
                description: 'Tu sais tout. Ouvre ta première position et regarde le classement bouger. Tu peux revoir ce tutoriel depuis ton profil.',
            },
        ],
    },
];

export default tutosConfig;
